import React, {useEffect, useState, useRef, useCallback} from 'react';
import HomeTabListContent from '../../home-list-tab-content';
import Tabs from '../../components/tab/tab';
import {useHomeStore} from '../store';

interface LotteryProps {
  scrollViewRef?: React.RefObject<any>;
  containerOffset?: number;
  scrollY?: number;
}

export default function Lottery({
  scrollViewRef,
  containerOffset = 0,
  scrollY = 0,
}: LotteryProps) {
  const {gameTypes, keralaList, digitList, matkaList, getLotteryGames} =
    useHomeStore();
  const [activeTab, setActiveTab] = useState(0);
  const [measuredTabsHeight, setMeasuredTabsHeight] = useState(0);
  const contentHeights = useRef<number[]>([]);
  const contentOffsets = useRef<number[]>([]);
  const isScrollingToTab = useRef(false);
  const lotteryContainerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getLotteryGames();
  }, [getLotteryGames]);

  // 计算累积偏移量
  const calculateOffsets = useCallback(() => {
    const offsets = [0];
    for (let i = 1; i < contentHeights.current.length; i++) {
      offsets[i] = offsets[i - 1] + contentHeights.current[i - 1];
    }
    contentOffsets.current = offsets;
  }, []);

  // 处理内容区域高度测量
  const handleMeasure = useCallback(
    (index: number, height: number) => {
      contentHeights.current[index] = height;
      // 当所有高度都测量完成时，计算偏移量
      if (
        contentHeights.current.filter(h => h > 0).length ===
        gameTypes.length - 1
      ) {
        calculateOffsets();
      }
    },
    [gameTypes.length, calculateOffsets],
  );

  // Tab点击时滚动到对应位置
  const handleTabClick = useCallback(
    (index: number) => {
      if (
        !scrollViewRef?.current ||
        contentOffsets.current[index] === undefined
      ) {
        setActiveTab(index);
        return;
      }

      isScrollingToTab.current = true;
      setActiveTab(index);

      // 计算目标滚动位置
      // 由于Tabs使用sticky定位，内容需要滚动到Tabs下方可见
      const lotteryTop = lotteryContainerRef.current?.offsetTop || 0;

      // 正确的计算：lottery位置 + 内容偏移 + tabs高度（确保内容显示在sticky tabs下方）
      const targetOffset =
        lotteryTop + contentOffsets.current[index] + measuredTabsHeight;

      scrollViewRef.current.scrollTo({
        y: targetOffset,
        animated: true,
      });

      // 延迟重置标记，避免滚动动画过程中的干扰
      setTimeout(() => {
        isScrollingToTab.current = false;
      }, 500);
    },
    [scrollViewRef, measuredTabsHeight],
  );

  // 监听滚动事件，更新当前激活的tab
  useEffect(() => {
    if (isScrollingToTab.current) {
      return;
    }

    const lotteryTop = lotteryContainerRef.current?.offsetTop || 0;
    const relativeScrollY = scrollY - lotteryTop - containerOffset;

    // 找到当前应该激活的tab
    let newActiveTab = 0;
    for (let i = contentOffsets.current.length - 1; i >= 0; i--) {
      if (relativeScrollY >= contentOffsets.current[i] - 50) {
        // 50px 的阈值
        newActiveTab = i;
        break;
      }
    }

    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
    }
  }, [scrollY, activeTab, containerOffset]);

  // 使用 ResizeObserver 测量 Tabs 高度
  useEffect(() => {
    if (!tabsRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setMeasuredTabsHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(tabsRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="mt-2" ref={lotteryContainerRef}>
      {gameTypes.length > 0 && (
        <div className="sticky top-0 bg-white z-[100]" ref={tabsRef}>
          <Tabs
            homeGames={gameTypes}
            activeTab={activeTab}
            scrollToCurrentGame={handleTabClick}
          />
        </div>
      )}
      <div>
        <HomeTabListContent
          homeGames={gameTypes}
          digitList={digitList}
          keralaList={keralaList}
          matkaList={matkaList}
          onMeasure={handleMeasure}
        />
      </div>
    </div>
  );
}
